<template>
  <div class="finetune-policy pb-3">
    <div class="finetune-policy__body">
      <div v-if="totalSelectableRules > 0" class="finetune-policy__select-all mb-3">
        <a-checkbox
          :checked="selectAllChecked"
          :indeterminate="selectAllIndeterminate"
          @change="onSelectAllCheckboxChange"
        >
          Select all
        </a-checkbox>
      </div>

      <div v-if="groups.length === 0" class="text-center text-muted py-4">
        No do-not-monitor suggestions for this report.
      </div>

      <template v-else-if="groups.length === 1">
        <PolicyGroupContent
          :group="groups[0]"
          :groupIndex="0"
          :selectedRules="selectedPolicyRules"
          :ruleEdits="policyRuleEdits"
          :groupRuleEdits="policyGroupRuleEdits"
          :editableRules="true"
          :showApplyButton="canApplyGroup(groups[0])"
          :applying="applyingPolicies"
          @toggle="onTogglePolicyRule(0, $event)"
          @change="onChangePolicyRule(0, $event)"
          @group-change="onChangeGroupedPolicyRule(0, $event)"
          @apply="onApplyGroup(groups[0], 0)"
          @proceed="onProceedToPolicy(groups[0], 0)"
        />
      </template>

      <a-tabs v-else type="card" :activeKey="activeTabKey" @change="onTabChange">
        <a-tab-pane
          v-for="(group, index) in groups"
          :key="String(index)"
          :tab="getTabLabel(group, index)"
        >
          <PolicyGroupContent
            :group="group"
            :groupIndex="index"
            :selectedRules="selectedPolicyRules"
            :ruleEdits="policyRuleEdits"
            :groupRuleEdits="policyGroupRuleEdits"
            :editableRules="true"
            :showApplyButton="canApplyGroup(group)"
            :applying="applyingPolicies"
            @toggle="onTogglePolicyRule(index, $event)"
            @change="onChangePolicyRule(index, $event)"
            @group-change="onChangeGroupedPolicyRule(index, $event)"
            @apply="onApplyGroup(group, index)"
            @proceed="onProceedToPolicy(group, index)"
          />
        </a-tab-pane>
      </a-tabs>
    </div>

    <div
      v-if="applicableGroupEntries.length > 0"
      class="finetune-policy__footer border-top bg-white py-3 mt-3"
    >
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <span class="text-muted mb-1 mb-md-0">
          {{ applicableSelectedSourceCount }} source rule(s) selected ·
          {{ applicableSelectedCount }} grouped rule(s) will be applied
        </span>
        <a-button
          type="primary"
          :loading="applyingPolicies"
          :disabled="applicableSelectedCount === 0"
          @click="onApplyAll"
        >
          Apply {{ applicableSelectedCount }} item(s)
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import PolicyGroupContent from '@shared/components/SuggestedPolicy/PolicyGroupContent.vue';
import policyRuleSelectionMixin from '@shared/mixins/policyRuleSelection.mixin';
import {
  filterNewRules,
  getPolicyGroupUrlIds,
  isContentLevelChangeAssetRule,
  mergeRulesWithSeverity,
  normalizeRules
} from '@shared/utils/policyRules';
import { getApiErrorMessage } from '@shared/utils/finetune';
import { stagePolicyPrefill } from '@shared/utils/policyPrefill';
import PolicyService from '../services/policy.service';

export default {
  name: 'FinetuneSuggestionDetailPolicy',
  components: {
    PolicyGroupContent
  },
  mixins: [policyRuleSelectionMixin],
  props: {
    detail: {
      type: Object,
      default: () => ({
        policy_suggestions: []
      })
    }
  },
  data() {
    return {
      activeTabKey: '0',
      applyingPolicies: false
    };
  },
  computed: {
    groups() {
      const groups = Array.isArray(this.detail.policy_suggestions)
        ? this.detail.policy_suggestions
        : [];
      return groups.filter((group) => this.getGroupRules(group).length > 0);
    },
    applicableGroupEntries() {
      return this.groups.reduce((entries, group, groupIndex) => {
        if (this.canApplyGroup(group)) {
          entries.push({ group, groupIndex });
        }
        return entries;
      }, []);
    },
    applicableSelectedCount() {
      return this.applicableGroupEntries.reduce(
        (total, { groupIndex }) =>
          total + this.getSelectedPolicyRules(groupIndex).length,
        0
      );
    },
    applicableSelectedSourceCount() {
      return this.applicableGroupEntries.reduce(
        (total, { group, groupIndex }) =>
          this.getGroupRules(group).reduce(
            (selectedTotal, _, ruleIndex) =>
              selectedTotal +
              (this.selectedPolicyRules[
                this.getPolicyRuleKey(groupIndex, ruleIndex)
              ] !== false
                ? 1
                : 0),
            total
          ),
        0
      );
    },
    selectedSourceCount() {
      return this.groups.reduce(
        (total, group, groupIndex) =>
          this.getGroupRules(group).reduce(
            (selectedTotal, _, ruleIndex) =>
              selectedTotal +
              (this.selectedPolicyRules[
                this.getPolicyRuleKey(groupIndex, ruleIndex)
              ] !== false
                ? 1
                : 0),
            total
          ),
        0
      );
    },
    totalSelectableRules() {
      return this.groups.reduce((total, group) => total + this.getGroupRules(group).length, 0);
    },
    selectAllChecked() {
      const total = this.totalSelectableRules;
      if (total === 0) return false;
      return this.selectedSourceCount === total;
    },
    selectAllIndeterminate() {
      return (
        this.selectedSourceCount > 0 &&
        this.selectedSourceCount < this.totalSelectableRules
      );
    }
  },
  watch: {
    detail: {
      immediate: true,
      handler() {
        this.activeTabKey = '0';
        this.initializeSelectedRules();
      }
    }
  },
  methods: {
    canApplyGroup(group) {
      return (
        group &&
        group.mode === 'existing' &&
        group.policyId !== null &&
        group.policyId !== undefined
      );
    },
    onTabChange(activeKey) {
      this.activeTabKey = activeKey;
    },
    getGroupRules(group) {
      return group.mode === 'existing' ? group.newRulesOnly || [] : group.rules || [];
    },
    getPolicySelectionRules(groupIndex) {
      const group = this.groups[groupIndex];
      return group ? this.getGroupRules(group) : [];
    },
    initializeSelectedRules() {
      this.resetPolicyRuleSelection(
        this.groups.length,
        (rule) => !isContentLevelChangeAssetRule(rule)
      );
    },
    onSelectAllCheckboxChange(e) {
      this.setAllPolicyRulesSelected(this.groups.length, e.target.checked);
    },
    getTabLabel(group, index) {
      if (group.mode === 'do_not_monitor') {
        const n = (group.urls || []).length;
        return `New policy (${n} URL${n === 1 ? '' : 's'})`;
      }
      if (group.mode === 'existing') {
        return `Policy ${group.policyName || `#${group.policyId}`} (${(group.urls || []).length})`;
      }
      return `New Policy ${index + 1} (${(group.urls || []).length})`;
    },
    buildExistingPolicyPayload(policy, rules) {
      return {
        name: policy.name,
        description: policy.description,
        text_string_appear: policy.text_string_appear || '',
        text_string_not_appear: policy.text_string_not_appear || '',
        rules,
        url_ids: Array.isArray(policy.urls)
          ? policy.urls.map((url) => String(url.id))
          : (policy.url_ids || []).map((id) => String(id))
      };
    },
    async applyGroup(group, groupIndex) {
      if (!this.canApplyGroup(group)) return 0;

      const selectedRules = normalizeRules(this.getSelectedPolicyRules(groupIndex));
      if (!selectedRules.length) return 0;

      const policy = await PolicyService.get(group.policyId);
      const newRules = filterNewRules(selectedRules, policy.rules || []);
      if (!newRules.length) return 0;
      const mergedRules = mergeRulesWithSeverity(policy.rules || [], newRules);
      await PolicyService.update(
        group.policyId,
        this.buildExistingPolicyPayload(policy, mergedRules)
      );
      return newRules.length;
    },
    onProceedToPolicy(group, groupIndex) {
      const selectedRules = this.getSelectedPolicyRules(groupIndex);
      if (!selectedRules.length) return;

      const normalizedRules = normalizeRules(selectedRules);
      let path;
      let payload;

      if (group.mode === 'existing') {
        payload = {
          mode: 'edit',
          policyId: String(group.policyId),
          rules: normalizedRules
        };
        path = `/policy/${group.policyId}`;
      } else {
        payload = {
          mode: 'new',
          url_ids: getPolicyGroupUrlIds(group),
          rules: normalizedRules
        };
        path = '/policy/add';
      }

      let handle;
      try {
        handle = stagePolicyPrefill(payload);
      } catch (_) {
        this.$message.error('Unable to transfer these rules to the policy page.');
        return;
      }

      const destination = `${path}?prefill=${encodeURIComponent(handle)}`;
      if (group.mode === 'existing') {
        const resolvedRoute = this.$router.resolve(destination);
        window.open(resolvedRoute.href, '_blank', 'noopener,noreferrer');
        return;
      }

      this.$router.push(destination);
    },
    onApplyGroup(group, groupIndex) {
      if (!this.canApplyGroup(group)) return;

      const count = this.getSelectedPolicyRules(groupIndex).length;
      if (count === 0) return;
      const target = `policy ${group.policyName || `#${group.policyId}`}`;
      this.$confirm({
        title: `Apply ${count} rule(s) to ${target}?`,
        okText: 'Apply',
        cancelText: 'Cancel',
        onOk: async () => {
          this.applyingPolicies = true;
          try {
            const applied = await this.applyGroup(group, groupIndex);
            if (applied > 0) {
              this.$message.success(`Applied ${applied} rule(s) to ${target}.`);
            } else {
              this.$message.info('No new rules needed to be applied.');
            }
          } catch (error) {
            this.$message.error(getApiErrorMessage(error, 'Failed to apply policy rules.'));
            throw error;
          } finally {
            this.applyingPolicies = false;
          }
        }
      });
    },
    onApplyAll() {
      const applicableGroups = this.applicableGroupEntries;
      const count = this.applicableSelectedCount;
      if (count === 0 || applicableGroups.length === 0) return;

      this.$confirm({
        title: `Apply ${count} rule(s) across ${applicableGroups.length} existing policy group(s)?`,
        okText: 'Apply all',
        cancelText: 'Cancel',
        onOk: async () => {
          this.applyingPolicies = true;
          let applied = 0;
          const failures = [];
          try {
            for (let index = 0; index < applicableGroups.length; index += 1) {
              const { group, groupIndex } = applicableGroups[index];
              try {
                applied += await this.applyGroup(group, groupIndex);
              } catch (error) {
                failures.push(error);
              }
            }
            if (applied > 0) {
              this.$message.success(`Applied ${applied} rule(s).`);
            }
            if (failures.length > 0) {
              this.$message.error(
                `${failures.length} policy group(s) failed. ${getApiErrorMessage(
                  failures[0],
                  'Failed to apply policy rules.'
                )}`
              );
            }
            if (applied === 0 && failures.length === 0) {
              this.$message.info('No new rules needed to be applied.');
            }
          } finally {
            this.applyingPolicies = false;
          }
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$finetune-detail-padding-x: 20px;

.finetune-policy__body {
  padding-left: $finetune-detail-padding-x;
  padding-right: $finetune-detail-padding-x;
}

.finetune-policy__footer {
  padding-left: $finetune-detail-padding-x;
  padding-right: $finetune-detail-padding-x;
}

.finetune-policy__select-all {
  font-weight: 500;
}
</style>
